import type { FunctionComponent, TargetedEvent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import { filterQueryChanged } from 'Core/actions/request';
import { searchRequestFilterQuerySelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import uiConfig from 'Models/uiConfig/uiConfig';
import { cloneSafe, preventFormSubmission } from 'Utils/components';

type Params = {
  value: string;
  pageType: string;
  inputNotEmpty: boolean;
  clearInput: () => void;
  onChange: (e: TargetedEvent<HTMLInputElement, Event>) => void;
  onSubmit: (e: TargetedEvent<HTMLFormElement, Event>) => void;
};

type Props = {
  template: TemplateFunction<Params>;
};

const FilterSearchBox: FunctionComponent<Props> = ({ template }) => {
  const dispatch = useDispatch();
  const rootRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState('');

  const filterQuery = useSelector(searchRequestFilterQuerySelector);

  useEffect(() => {
    setQuery(filterQuery ?? '');
  }, [filterQuery]);

  const onChange = (e: TargetedEvent<HTMLInputElement, Event>) => setQuery(e.currentTarget.value);

  const onSubmit = (e: TargetedEvent<HTMLFormElement, Event>) => {
    preventFormSubmission(e);
    dispatch(filterQueryChanged(query));
  };

  const clearInput = () => {
    setQuery('');
  };

  const component = template.call({
    value: query,
    pageType: uiConfig.pageType,
    inputNotEmpty: !!query,
    clearInput: () => {
      clearInput();
      rootRef.current?.querySelector('input')?.focus();
    },
    onChange,
    onSubmit,
  });

  return cloneSafe(component, rootRef);
};

export default FilterSearchBox;
